/*  MUI  */
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Typography
} from '@mui/material';

/*  Shared  */
import { PermissionPicker } from 'application/Shared/Components/Pickers';
import { Helpers, Notify, Transition } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { Requests, WelcomeEmail } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/* Internal */
import { ProvidersUsersTable } from './internal/ProviderUsersTable';

class ProviderUsers extends Component {
    constructor(props) {
        super(props);
        this.state = {
            defaultPermission: 0,
            importModal: {
                permissionId: 0,
                sendWelcomeEmail: false,
                show: false,
                user: {}
            },
            loading: true,
            rowMap: {},
            users: []
        };

        /* requests */
        this.requests = new Requests();
        this.WelcomeEmail = new WelcomeEmail();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmount = true;
        this.unload();
    }

    load = async () => {
        await this.loadUsers();
        await this.loadPermissions();

        if (!this.unmount) {
            this.setState({ loading: false });
        }
    };

    unload = () => {
        this.requests.cancel();
        this.WelcomeEmail.cancel();
    };

    loadUsers = async () => {
        if (this.unmount) {
            return;
        }

        const { match, providerRoute, t } = this.props;
        const { clientId } = match.params;

        const { rowMap } = this.state;

        const params =
            providerRoute === 'azure'
                ? {
                      includeSystemUser: true,
                      maxCacheAge: 15 * 60, // Cached users must be fetched with-in last 15 mins
                      userType: '*'
                  }
                : {};

        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get(`/${providerRoute}/users`, {
                cancelToken: this.requests.token(),
                params
            });
            if (Helpers.isset(response, 'data', 'data', 'users')) {
                const { users } = response.data.data;
                this.setState({ users });
                users.forEach((user) => {
                    const { exists, id } = user;
                    rowMap[id] = {
                        ...user,
                        imported: !!exists
                    };
                });
                this.setState({ rowMap });
            }
        } catch (err) {
            debug.e(
                t('Provider.MicrosoftConfiguration.error_loading_users'),
                err.toString()
            );
        }
    };

    loadPermissions = async () => {
        if (this.unmount) {
            return;
        }

        const { match } = this.props;
        const { clientId } = match.params;

        const params = { size: -1 };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/permission', {
                cancelToken: this.requests.token(),
                params
            });
            if (!Helpers.isset(response, 'data', 'data', 0, 'id')) {
                return;
            }

            const { data } = response.data;
            const noUIAccess = data.filter(
                (datum) => datum.name === 'No UI Access'
            );
            const defaultPermission = noUIAccess[0]?.id || 0;
            this.setState({ defaultPermission });
        } catch (err) {
            debug.e('Error loading user permissions', err.toString());
        }
    };

    /* User Management */
    importUser = async (id, importData = false) => {
        const { t, match, providerRoute, session } = this.props;
        const { clientId } = match.params;
        const { users } = this.state;

        // Add the loader
        const loadingUsers = users.map((a) => ({
            ...a,
            loading: id === a.id ? true : a.loading
        }));
        this.setState({ users: loadingUsers });

        let payload = { id };
        if (importData) {
            const { permissionId } = importData;
            payload = {
                ...payload,
                permissionId
            };
        }
        if (clientId) {
            payload.platformTenantId = clientId;
        }

        try {
            await axios.post(`/${providerRoute}/tenantUser/`, payload);

            const updatedUsers = users.map((a) => ({
                ...a,
                exists: id === a.id ? !a.exists : a.exists,
                loading: id === a.id ? false : a.loading
            }));
            this.setState({ users: updatedUsers });
            if (!importData) {
                Notify.s(t('Provider.Shared.success_user_added'));
            }
        } catch (err) {
            Notify.e(t('Provider.Shared.error_saving_user'));
        }

        if (importData) {
            const { user, sendWelcomeEmail } = importData;
            if (sendWelcomeEmail) {
                try {
                    await this.WelcomeEmail.sendWelcomeEmail({
                        clientId,
                        emails: [user.userPrincipalName || user.email],
                        session,
                        t
                    });
                    Notify.s(t('Users.sentInvitation', { email: user.email }));
                } catch (e) {
                    Notify.e(e);
                }
            }
        }

        this.showImportModal(false);
    };

    showImportModal = (show, user) => {
        const { defaultPermission } = this.state;
        this.setState({
            importModal: {
                permissionId: defaultPermission,
                sendWelcomeEmail: false,
                show,
                user
            }
        });
    };

    renderImport() {
        const { t } = this.props;
        const { importModal } = this.state;
        const { show, user, sendWelcomeEmail, permissionId } = importModal;

        if (!show) {
            return null;
        }

        return (
            <Modal
                open={show}
                aria-labelledby="providerUsersImportModal"
                TransitionComponent={Transition}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') {
                        this.showImportModal(false);
                    }
                }}
                title={t('Common.importUser')}
            >
                {show && (
                    <>
                        <ModalBody data-testid="providerUsersImportModal">
                            {show && (
                                <Alert
                                    severity="info"
                                    icon={false}
                                    style={{ marginBottom: '8px' }}
                                >
                                    {`${t('Common.importing_user')} - ${
                                        user.userPrincipalName || user.email
                                    }`}
                                </Alert>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendWelcomeEmail}
                                        data-testid="providerUsersImportModalSendEmailCheckbox"
                                        onChange={() =>
                                            this.setState({
                                                importModal: {
                                                    ...importModal,
                                                    sendWelcomeEmail:
                                                        !sendWelcomeEmail
                                                }
                                            })
                                        }
                                    />
                                }
                                label={t('Users.send_welcome_email')}
                            />
                            <br />
                            <Typography color="textSecondary" fontWeight={600}>
                                {t('Common.permission')}
                            </Typography>
                            <PermissionPicker
                                value={permissionId}
                                onChange={(val) =>
                                    this.setState({
                                        importModal: {
                                            ...importModal,
                                            permissionId: val
                                        }
                                    })
                                }
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="primary"
                                data-testid="providerUsersImportModalCancelBtn"
                                data-trackid="admin_users_providerUsers_usersImport_import_cancel"
                                onClick={() => this.showImportModal(false)}
                                variant="outlined"
                            >
                                {t('Common.cancel')}
                            </Button>
                            <Button
                                color="primary"
                                data-testid="providerUsersImportModalImportBtn"
                                data-trackid="admin_users_providerUsers_usersImport_import_save"
                                onClick={() =>
                                    this.importUser(user.id, importModal)
                                }
                                variant="contained"
                            >
                                {t('Provider.Shared.import')}
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </Modal>
        );
    }

    render() {
        const { session, t, translationProvider } = this.props;
        const { loading, rowMap, users } = this.state;

        return (
            <Grid container spacing={3} data-testid="providerUsers">
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                        <Typography variant="h3">
                            {t(`Provider.${translationProvider}.users_title`)}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Paper elevation={0} variant="outlined">
                        <Typography>
                            {t(
                                `Provider.${translationProvider}.Users.sync_page`
                            )}
                        </Typography>
                        <ProvidersUsersTable
                            loading={loading}
                            renderRow={this.renderRow}
                            rowMap={rowMap}
                            translationProvider={translationProvider}
                            users={users}
                            importUser={this.importUser}
                            session={session}
                            showImportModal={this.showImportModal}
                        />
                    </Paper>
                    {this.renderImport()}
                </Grid>
            </Grid>
        );
    }
}
export default withRouter(withTranslation()(ProviderUsers));
